/** The layer stack of one canvas: order, visibility, lock and geometry (`OVL-027`). */

import { useMemo, useState } from "react";

import { EmptyState, Field, IconButton, Input, Stack, StatusBadge, Toolbar } from "@/components/ui";
import { useCanvasState } from "@/lib/useCanvasState";

import {
  DownIcon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  UnlockIcon,
  UpIcon,
} from "./LayerIcons";
import { Section } from "./tray/Section";
import { ToBottomIcon, ToTopIcon } from "./tray/TrayIcons";
import { TrayRow } from "./tray/TrayRow";

import { Mono, Muted } from "../components/Text";
import type { AddedActivityRow } from "../types";

import { FULL_CANVAS, moved, reordered, without, type Placement } from "./lib/arrangement";
import { writeDrag } from "./lib/drop";
import { nodesOf } from "./lib/scenes";
import type { StageSelection } from "./SpaceCanvas";

/** Which layers this person has pinned down. Editor state, never stored. */
export type Locks = Readonly<Record<string, boolean>>;

/** A row's rectangle, in whole percent, as the four axes a geometry readout names. */
function axes(held: Placement): readonly { key: string; value: number }[] {
  return [
    { key: "x", value: Math.round(held.left * 100) },
    { key: "y", value: Math.round(held.top * 100) },
    { key: "w", value: Math.round(held.width * 100) },
    { key: "h", value: Math.round(held.height * 100) },
  ];
}

/** The compact "x 0  y 70  w 67  h 30" readout: mono figures, muted axis labels. */
function GeometryReadout({ held }: { readonly held: Placement }) {
  return (
    <span className="cx-layer-geo">
      {axes(held).map((axis) => (
        <span className="cx-layer-geo-item" key={axis.key}>
          <span className="cx-layer-geo-unit">{axis.key}</span>
          <span className="cx-layer-geo-value">{axis.value}</span>
        </span>
      ))}
    </span>
  );
}

/** A four-field geometry editor for the selected layer, in whole percent. */
function Geometry({
  held,
  disabled,
  onChange,
}: {
  readonly held: Placement;
  readonly disabled: boolean;
  readonly onChange: (next: Placement) => void;
}) {
  const fields = [
    { key: "left" as const, label: "X" },
    { key: "top" as const, label: "Y" },
    { key: "width" as const, label: "W" },
    { key: "height" as const, label: "H" },
  ];

  return (
    <div className="cx-geometry">
      {fields.map((field) => (
        <Field label={field.label} key={field.key}>
          <Input
            type="number"
            mono
            min={0}
            max={100}
            step={1}
            disabled={disabled}
            value={Math.round(held[field.key] * 100)}
            aria-label={`${field.label} of the selected layer`}
            onChange={(event) => {
              const raw = Number(event.target.value);
              if (Number.isNaN(raw)) return;
              onChange({ ...held, [field.key]: Math.min(Math.max(raw, 0), 100) / 100 });
            }}
          />
        </Field>
      ))}
    </div>
  );
}

/** The instance a second click picked, read from the one scene document that names it. */
function NodeDetail({
  node,
  rows,
  canvasKind,
}: {
  readonly node: StageSelection;
  readonly rows: readonly AddedActivityRow[];
  readonly canvasKind: string;
}) {
  const row = rows.find((one) => one.activity_id === node.activityId);
  const mountId = row?.mount?.mount_id ?? "";
  const { state } = useCanvasState(mountId, undefined, canvasKind);

  const kind = useMemo(() => {
    if (node.nodeId === undefined) return undefined;
    for (const document of state?.scenes ?? []) {
      if (document.canvas_kind !== canvasKind) continue;
      const hit = nodesOf(document).find((one) => one.id === node.nodeId);
      if (hit !== undefined) return hit.kind;
    }
    return undefined;
  }, [state, node.nodeId, canvasKind]);

  return (
    <Section title="Node">
      <Stack gap={2}>
        <div className="cx-brings">
          <span className="cx-key">Node</span>
          <Mono>{node.nodeId ?? node.nodeKey ?? ""}</Mono>
        </div>
        {kind === undefined ? null : (
          <div className="cx-brings">
            <span className="cx-key">Type</span>
            <Muted small>{kind}</Muted>
          </div>
        )}
        <div className="cx-brings">
          <span className="cx-key">Layer</span>
          <Muted small>{row?.name ?? ""}</Muted>
        </div>
      </Stack>
    </Section>
  );
}

export interface LayersPanelProps {
  /** Every activity that draws on this canvas, back to front. */
  readonly rows: readonly AddedActivityRow[];
  /** The canvas being drawn, which is what a picked node's own document names itself against. */
  readonly canvasKind: string;
  readonly placements: Readonly<Record<string, Placement>>;
  readonly locks: Locks;
  readonly selectedId: string | null;
  /** The drawn instance a second click picked, shown read-only below the stack. */
  readonly node: StageSelection | null;
  readonly onSelect: (addedActivityId: string) => void;
  readonly onLock: (addedActivityId: string, locked: boolean) => void;
  readonly onCommit: (placements: Record<string, Placement>) => void;
  /** Whether a rectangle means anything here. A flowed canvas has order and nothing else. */
  readonly placeable?: boolean;
  readonly busy: boolean;
}

/** The stack, front at the top, the way every layer panel a person has used is arranged. */
export function LayersPanel({
  rows,
  canvasKind,
  placements,
  locks,
  selectedId,
  node,
  onSelect,
  onLock,
  onCommit,
  placeable = true,
  busy,
}: LayersPanelProps) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  if (rows.length === 0) {
    return <EmptyState bare headline="Nothing arranged" />;
  }

  const order = rows.map((row) => row.added_activity_id);
  const front = [...rows].reverse();
  const reorderable = front.length > 1;

  const shift = (addedActivityId: string, by: number): void => {
    const next = reordered(order, placements, addedActivityId, by);
    if (next !== null) onCommit(next);
  };

  const drop = (onto: string): void => {
    if (dragging === null || dragging === onto) return;
    const next = moved(order, placements, dragging, onto);
    if (next !== null) onCommit(next);
  };

  const write = (addedActivityId: string, next: Placement): void => {
    onCommit({ ...placements, [addedActivityId]: next });
  };

  return (
    <Stack gap={0}>
      {front.map((row, index) => {
        const id = row.added_activity_id;
        const held = placements[id] ?? FULL_CANVAS;
        const arranged = placements[id] !== undefined;
        const locked = locks[id] === true;
        const chosen = selectedId === id;
        const draggedOver = over === id && dragging !== null && dragging !== id;

        return (
          <div
            key={id}
            className="cx-layer-row"
            draggable={!busy}
            onClick={(event) => {
              const target = event.target as HTMLElement;
              if (target.closest("button, input")) return;
              onSelect(id);
            }}
            onDragStart={(event) => {
              setDragging(id);
              writeDrag(event.dataTransfer, { from: "added", addedActivityId: id });
            }}
            onDragEnd={() => {
              setDragging(null);
              setOver(null);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(id);
            }}
            onDrop={(event) => {
              event.preventDefault();
              drop(id);
              setDragging(null);
              setOver(null);
            }}
          >
            <TrayRow
              dim={held.hidden}
              className={[chosen ? "cx-tray-row-selected" : "", draggedOver ? "cx-tray-row-over" : ""]
                .filter((one) => one !== "")
                .join(" ")}
              title={
                <button
                  type="button"
                  className="cx-layer-name"
                  aria-pressed={chosen}
                  onClick={() => {
                    onSelect(id);
                  }}
                >
                  {row.name}
                </button>
              }
              badge={held.hidden ? <StatusBadge>Hidden</StatusBadge> : undefined}
              meta={placeable ? <GeometryReadout held={held} /> : undefined}
              actions={
                <>
                  <Toolbar>
                    <IconButton
                      label={held.hidden ? `Show ${row.name}` : `Hide ${row.name}`}
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        write(id, { ...held, hidden: !held.hidden });
                      }}
                    >
                      {held.hidden ? <EyeOffIcon /> : <EyeIcon />}
                    </IconButton>
                    <IconButton
                      label={locked ? `Unlock ${row.name}` : `Lock ${row.name}`}
                      size="sm"
                      onClick={() => {
                        onLock(id, !locked);
                      }}
                    >
                      {locked ? <LockIcon /> : <UnlockIcon />}
                    </IconButton>
                  </Toolbar>
                  {reorderable ? (
                    <Toolbar>
                      <IconButton
                        label={`Bring ${row.name} to the front`}
                        size="sm"
                        disabled={busy || index === 0}
                        onClick={() => {
                          shift(id, index);
                        }}
                      >
                        <ToTopIcon />
                      </IconButton>
                      <IconButton
                        label={`Bring ${row.name} forward`}
                        size="sm"
                        disabled={busy || index === 0}
                        onClick={() => {
                          shift(id, 1);
                        }}
                      >
                        <UpIcon />
                      </IconButton>
                      <IconButton
                        label={`Send ${row.name} backward`}
                        size="sm"
                        disabled={busy || index === front.length - 1}
                        onClick={() => {
                          shift(id, -1);
                        }}
                      >
                        <DownIcon />
                      </IconButton>
                      <IconButton
                        label={`Send ${row.name} to the back`}
                        size="sm"
                        disabled={busy || index === front.length - 1}
                        onClick={() => {
                          shift(id, index - (front.length - 1));
                        }}
                      >
                        <ToBottomIcon />
                      </IconButton>
                    </Toolbar>
                  ) : null}
                </>
              }
              detail={
                chosen ? (
                  <>
                    {placeable ? (
                      <Geometry
                        held={held}
                        disabled={busy || locked}
                        onChange={(next) => {
                          write(id, next);
                        }}
                      />
                    ) : (
                      <Muted small>This canvas flows, so the order decides and a rectangle does not.</Muted>
                    )}
                    {placeable && arranged ? (
                      <button
                        type="button"
                        className="cx-layer-reset"
                        disabled={busy}
                        onClick={() => {
                          onCommit(without(placements, id));
                        }}
                      >
                        Reset to the default
                      </button>
                    ) : (
                      <Muted small>Unarranged: the compositor decides.</Muted>
                    )}
                  </>
                ) : undefined
              }
            />
          </div>
        );
      })}
      {node === null ? null : <NodeDetail node={node} rows={rows} canvasKind={canvasKind} />}
    </Stack>
  );
}
